import React from 'react';
import PropTypes from 'prop-types';
import { Spinner } from 'react-bootstrap';

/**
 * ModalFooter — Reusable button row for CommonModal dialogs.
 *
 * Variants:
 *  1. Save/Cancel  — used in Add / Edit forms
 *  2. Delete/Cancel — used in Delete confirmation dialogs
 *
 * Usage (Save form):
 *   <ModalFooter
 *     onCancel={handleClose}
 *     submitLabel="Save Chapter"
 *     loading={isSubmitting}
 *   />
 *
 * Usage (Delete confirm):
 *   <ModalFooter
 *     onCancel={() => setTarget(null)}
 *     onDelete={handleDelete}
 *     loading={deleteLoading}
 *   />
 */
const ModalFooter = ({
  // Cancel
  onCancel,
  cancelLabel,
  cancelDisabled,

  // Submit / Save (form type="submit" or button)
  onSubmit,
  submitLabel,
  submitType,
  loading,

  // Delete (destructive confirmation)
  onDelete,
  deleteLabel,
  isValid,
  submitDisabled,
  loadingLabel
}) => {
  const isDeleteMode = !!onDelete;

  return (
    <div className={`modal-footer-actions ${!isDeleteMode ? 'mfa-form-mode' : 'mfa-delete-mode'}`}>
      {/* Cancel */}
      <button type="button" className="mfa-btn mfa-cancel" onClick={onCancel} disabled={cancelDisabled || loading}>
        {cancelLabel || 'Cancel'}
      </button>

      {/* Submit / Save */}
      {!isDeleteMode && (
        <button
          type={submitType || 'submit'}
          className="mfa-btn mfa-save"
          onClick={onSubmit}
          disabled={loading || submitDisabled || isValid === false}
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              {loadingLabel || 'Processing...'}
            </>
          ) : (
            submitLabel || 'Save'
          )}
        </button>
      )}

      {/* Delete */}
      {isDeleteMode && (
        <button type="button" className="mfa-btn mfa-delete" onClick={onDelete} disabled={loading}>
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              {loadingLabel || 'Deleting...'}
            </>
          ) : (
            deleteLabel || 'Delete'
          )}
        </button>
      )}
    </div>
  );
};

ModalFooter.propTypes = {
  onCancel: PropTypes.func.isRequired,
  cancelLabel: PropTypes.string,
  cancelDisabled: PropTypes.bool,
  onSubmit: PropTypes.func,
  submitLabel: PropTypes.string,
  submitType: PropTypes.string,
  loading: PropTypes.bool,
  onDelete: PropTypes.func,
  deleteLabel: PropTypes.string,
  isValid: PropTypes.bool,
  submitDisabled: PropTypes.bool,
  loadingLabel: PropTypes.string
};

export default ModalFooter;
